import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Stack } from '@mui/material';

import { BulkReAssignRoleApiResponse, AccessCentreUser } from 'domains/accessCentre/types';
import BulkActionBar from 'domains/core/components/BulkActionBar';
import SearchField from 'domains/core/components/SearchField';
import { SettingsSectionHeaderIcon } from 'domains/core/components/SettingsHeader';
import SettingsPageLayout from 'domains/core/components/SettingsPageLayout';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import TableEmptyState from 'domains/core/components/Table/TableEmptyState';
import TableOffsetPagination from 'domains/core/components/TableOffsetPagination';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useGetUsersList from 'hooks/queries/useGetUsersList';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useDebounce from 'hooks/useDebounce';
import { SortDirection } from 'models/enums';
import { SEARCH_DEBOUNCE_MS, ACCESS_CENTRE_LIST_PAGE_SIZE, ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS } from 'utils';

import UsersBulkChangeRoleDialog from './UsersBulkChangeRoleDialog';
import UsersTable from './UsersTable';

const compareAccessCentreUsers = (
    a: AccessCentreUser,
    b: AccessCentreUser,
    sortColumn: string,
    sortDirection: SortDirection
): number => {
    const mul = sortDirection === SortDirection.ASCENDING ? 1 : -1;
    switch (sortColumn) {
        case 'name':
        case 'email':
        case 'role_name':
            return (
                mul *
                String(a[sortColumn] ?? '').localeCompare(String(b[sortColumn] ?? ''), undefined, {
                    sensitivity: 'base',
                })
            );
        case 'last_modified_timestamp':
            return (
                mul * (new Date(a.last_modified_timestamp).getTime() - new Date(b.last_modified_timestamp).getTime())
            );
        default:
            return 0;
    }
};

const UsersPage = () => {
    const { canManageAccessManagement } = useAccessCentrePermissions();
    const { data: featureSettings } = useFeatureSettings();
    const allowInlineUserNameEdit = canManageAccessManagement && featureSettings?.platformIntegrated === false;
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(ACCESS_CENTRE_LIST_PAGE_SIZE);
    const [sortColumnState, setSortColumnState] = useState('last_modified_timestamp');
    const [sortDirectionState, setSortDirectionState] = useState(SortDirection.DESCENDING);
    const [selectedById, setSelectedById] = useState<Record<string, AccessCentreUser>>({});
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [isBulkChangeRoleDialogOpen, setIsBulkChangeRoleDialogOpen] = useState(false);
    const [bulkAssignSuccessMessage, setBulkAssignSuccessMessage] = useState<string | null>(null);
    const [roleAssignSuccessMessage, setRoleAssignSuccessMessage] = useState<string | null>(null);

    const offset = (page - 1) * rowsPerPage;

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        setPage(1);
    }, [rowsPerPage]);

    const { data, isLoading, isSuccess, isRefetching } = useGetUsersList({
        limit: rowsPerPage,
        offset,
        searchTerm: debouncedSearch.trim() || undefined,
        sortColumn: sortColumnState,
        sortDirection: sortDirectionState,
        reactQueryOptions: {
            keepPreviousData: true,
            enabled: !showSelectedOnly,
        },
    });

    const onSort = (sortColumn: string, direction: SortDirection) => {
        setSortColumnState(sortColumn);
        setSortDirectionState(direction);
        setPage(1);
    };

    const apiUsers = useMemo(() => data?.data ?? [], [data]);
    const apiTotal = data?.total ?? 0;

    const sortedSelectedUsers = useMemo(() => {
        const list = Object.values(selectedById);
        return [...list].sort((a, b) => compareAccessCentreUsers(a, b, sortColumnState, sortDirectionState));
    }, [selectedById, sortColumnState, sortDirectionState]);

    const usersForTable = useMemo(() => {
        if (showSelectedOnly) {
            return sortedSelectedUsers.slice(offset, offset + rowsPerPage);
        }
        return apiUsers;
    }, [showSelectedOnly, sortedSelectedUsers, offset, rowsPerPage, apiUsers]);

    const totalForPagination = showSelectedOnly ? sortedSelectedUsers.length : apiTotal;
    const totalPages = Math.max(1, Math.ceil(totalForPagination / rowsPerPage));
    const isInitialLoading = !showSelectedOnly && isLoading && data === undefined;
    const tableLoading = showSelectedOnly ? false : isLoading || isRefetching;

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const selectedCount = sortedSelectedUsers.length;
    const selectedUserIds = useMemo(() => sortedSelectedUsers.map((u) => u.id), [sortedSelectedUsers]);

    useEffect(() => {
        if (showSelectedOnly && selectedCount === 0) {
            setShowSelectedOnly(false);
        }
    }, [showSelectedOnly, selectedCount]);

    const handleToggleUser = useCallback((user: AccessCentreUser) => {
        setSelectedById((prev) => {
            const next = { ...prev };
            if (next[user.id]) {
                delete next[user.id];
            } else {
                next[user.id] = user;
            }
            return next;
        });
    }, []);

    const handleDismissBulkBar = useCallback(() => {
        setSelectedById({});
        setShowSelectedOnly(false);
        setIsBulkChangeRoleDialogOpen(false);
    }, []);

    const handleShowSelectedOnlyChange = useCallback((show: boolean) => {
        setShowSelectedOnly(show);
        setPage(1);
    }, []);

    const handleBulkChangeRole = useCallback(() => {
        setIsBulkChangeRoleDialogOpen(true);
    }, []);

    const handleBulkChangeRoleDialogClose = useCallback(() => {
        setIsBulkChangeRoleDialogOpen(false);
    }, []);

    const handleBulkAssignSuccess = useCallback((response: BulkReAssignRoleApiResponse) => {
        const n = response.ids.length;
        const userWord = n === 1 ? 'user' : 'users';
        setBulkAssignSuccessMessage(`${n} ${userWord} assigned ${response.role_name} role`);
        setSelectedById({});
        setShowSelectedOnly(false);
    }, []);

    const handleUserRoleAssignSuccess = useCallback((message: string) => {
        setRoleAssignSuccessMessage(message);
    }, []);

    const previousPage = () => {
        if (page > 1) {
            setPage((p) => p - 1);
        }
    };

    const nextPage = () => {
        if (page < totalPages) {
            setPage((p) => p + 1);
        }
    };

    const emptySearch =
        !showSelectedOnly && isSuccess && usersForTable.length === 0 && debouncedSearch.trim().length > 0;

    /** Same pattern as ContentPageLayout; search is N/A while "selected only" (list query disabled). */
    const isHeaderControlsDisabled = !showSelectedOnly && (isLoading || isRefetching);

    const headerOthers = (
        <Stack direction="row" alignItems="center">
            <SearchField
                placeholder="Search users..."
                tooltipText="Search users by name, email, or role..."
                value={searchTerm}
                onChange={setSearchTerm}
                disabled={isHeaderControlsDisabled}
                dataTestId="access-center-users-search-input"
            />
        </Stack>
    );

    const bulkSelect = canManageAccessManagement
        ? {
              isUserSelected: (id: string) => Boolean(selectedById[id]),
              onToggleUser: handleToggleUser,
          }
        : undefined;

    return (
        <Stack data-testid="access-center-users-page" role="main" aria-label="Users and roles - Users">
            {roleAssignSuccessMessage && (
                <SuccessSnackbar key={roleAssignSuccessMessage} successMessage={roleAssignSuccessMessage} />
            )}
            {bulkAssignSuccessMessage && (
                <SuccessSnackbar key={bulkAssignSuccessMessage} successMessage={bulkAssignSuccessMessage} />
            )}
            <UsersBulkChangeRoleDialog
                open={isBulkChangeRoleDialogOpen}
                onClose={handleBulkChangeRoleDialogClose}
                selectedCount={selectedCount}
                selectedUserIds={selectedUserIds}
                selectedUsers={sortedSelectedUsers}
                onAssignSuccess={handleBulkAssignSuccess}
            />
            <SettingsPageLayout header="users" headerIcon={<SettingsSectionHeaderIcon />} headerOthers={headerOthers}>
                <>
                    <UsersTable
                        users={usersForTable}
                        isLoading={tableLoading}
                        showChangeRole={canManageAccessManagement}
                        allowInlineUserNameEdit={allowInlineUserNameEdit}
                        bulkSelect={bulkSelect}
                        onUserRoleAssignSuccess={handleUserRoleAssignSuccess}
                        onSort={onSort}
                    />
                    {emptySearch && (
                        <Box data-testid="access-center-users-empty-search">
                            <TableEmptyState
                                searchTerm={debouncedSearch.trim()}
                                noResultsFoundText="no match found"
                                noResultsFoundSubtitle="Check the spelling or try a different search term"
                            />
                        </Box>
                    )}
                    {!isInitialLoading && !emptySearch && (
                        <TableOffsetPagination
                            page={page}
                            totalPages={totalPages}
                            onPreviousPage={previousPage}
                            onNextPage={nextPage}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={setRowsPerPage}
                            testIdPrefix="access-center-users-pagination"
                        />
                    )}
                </>
            </SettingsPageLayout>
            {canManageAccessManagement && selectedCount > 0 && (
                <BulkActionBar
                    selectedCount={selectedCount}
                    actionButtonText="Change Role"
                    onBulkActionClick={handleBulkChangeRole}
                    actionButtonTestId="access-center-users-bulk-change-role"
                    filterToggle={{
                        checked: showSelectedOnly,
                        onChange: handleShowSelectedOnlyChange,
                        label: 'Show selected only',
                    }}
                    onDismiss={handleDismissBulkBar}
                />
            )}
        </Stack>
    );
};

export default UsersPage;
